import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery, 
    useTheme,
    TableCell as MuiTableCell,
    styled,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton
} from '@mui/material'
import useAccessCode from '../hooks/useAccessCode'
import { useNavigate } from 'react-router-dom'
import { IHistoryAccessCode } from '../models/codes'
import { CustomModal } from '../components/Modal'
import { Close, Done, DoneAll, Home, Image, Videocam } from '@mui/icons-material'

import { convertDate } from '../utils/date'
import { TableHeaderCell } from '../components/Table/TableHeaderCell'
import { ConfirmAndChild } from '../components/ConfirmWithChilds'

// Celda ultra compacta para móviles
const MobileTableCell = styled(MuiTableCell)(({ theme }) => ({
    padding: '4px 6px',
    fontSize: '0.7rem',
    lineHeight: '1.1',
    '&:first-of-type': {
        paddingLeft: '6px'
    },
    '&:last-of-type': {
        paddingRight: '6px'
    }
}))

const VIDEO_EXT = ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v', 'avi', 'mkv', '3gp']

const getFileKind = (url?: string): 'video' | 'foto' | null => {
    if (!url || !url.trim()) return null
    const clean = url.split('?')[0].split('#')[0]
    const ext = clean.substring(clean.lastIndexOf('.') + 1).toLowerCase()
    return VIDEO_EXT.includes(ext) ? 'video' : 'foto'
}

export const HistorialAcceso = () => {
    const navigate = useNavigate()
    const { getHistory, deleteAccessCode } = useAccessCode()
    const [history, setHistory] = useState<IHistoryAccessCode[]>([])

    const [itemSelect, setItemSelect] = useState<IHistoryAccessCode>({} as IHistoryAccessCode)
    const [confirm, setConfirm] = useState<boolean>(false)

    const [openModal, setOpenModal] = useState(false)
    const [modalMessage, setModalMessage] = useState('')

    const [fileViewer, setFileViewer] = useState<{ open: boolean, url: string, kind: 'video' | 'foto', codigo: string }>(
        { open: false, url: '', kind: 'foto', codigo: '' }
    )
    const [fileError, setFileError] = useState(false)

    const hasArchivos = history.some(item => getFileKind(item.url_Archivo) !== null)

    const handleOpenFile = (item: IHistoryAccessCode) => {
        const kind = getFileKind(item.url_Archivo)
        if (!kind) return
        setFileError(false)
        setFileViewer({ open: true, url: item.url_Archivo!, kind, codigo: item.codigo_Acceso })
    }
    const handleCloseFile = () => setFileViewer(prev => ({ ...prev, open: false }))

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

    const handleOpen = () => setOpenModal(true)
    const handleClose = () => {
        setOpenModal(false)
        navigate('/home')
    }

    useEffect(() => {
        (async () => {
            let history = await getHistory()
            if (history) {
                setHistory(history)
            } else {
                setModalMessage('Error al obtener el historial')
                handleOpen()
            }
        })()
    }, [])

    const AnularReserva = (codigo) => async () => {
        let code = await deleteAccessCode(codigo)
        if (code && code.success) {
            setModalMessage('Acceso anulado')
            setHistory(history.filter(item => item.codigo_Acceso !== codigo))
        } else if (code) {
            setModalMessage(code.message)
        } else {
            setModalMessage('Error de sistema al anular el código de acceso')
        }
        handleOpen()
    }

    const getStatusIcon = (item: IHistoryAccessCode) => {
        const hoy = new Date()
        const fechaVisita = new Date(item.fecha_Visita.split(' ')[0])

        if (item.fecha_Ingreso || item.fecha_Salida) {
            return {
                icon: <DoneAll fontSize="small" sx={{ color: 'blue' }} />,
                tooltip: 'Acceso utilizado'
            }
        }

        if (fechaVisita.getTime() >= hoy.setHours(0, 0, 0, 0)) {
            return {
                icon: <Done fontSize="small" sx={{ color: '#FFD600' }} />, 
                tooltip: 'Pendiente - Se puede anular'
            }
        }

        return {
            icon: <Done fontSize="small" color="disabled" />,
            tooltip: 'No anulable - Fecha pasada'
        }
    }
    const isRowClickable = (item: IHistoryAccessCode) => {
        const hoy = new Date()
        const fechaVisita = new Date(item.fecha_Visita.split(' ')[0])
        const hoyMidnight = hoy.setHours(0, 0, 0, 0)
        return !item.fecha_Ingreso && !item.fecha_Salida && fechaVisita.getTime() >= hoyMidnight
    }

    return (
        <>
            <ConfirmAndChild
                message={
                    <center>
                        
                        <Typography variant="body1">¿Anular código de acceso?</Typography>
                        <Typography variant="h4">{itemSelect.codigo_Acceso}</Typography>

                    </center>
                }
                okText='Anular'
                okAction={AnularReserva(itemSelect.codigo_Acceso)}
                show={confirm}
                setShow={setConfirm}
            />

            <Box textAlign="center" display="flex" alignItems="center" flexDirection='column' p={1}>
                <Typography variant={isSmallScreen ? "h6" : "h5"} gutterBottom>
                    Historial de Accesos
                </Typography>

                <Box width="100%" overflow="auto">
                    <Table
                        size="small"
                        sx={{
                            minWidth: isSmallScreen ? '100%' : 800,
                            '& .MuiTableCell-root': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            },
                            '& .MuiTableHead-root': {
                                '& .MuiTableCell-root': {
                                    fontSize: isSmallScreen ? '0.75rem' : '0.8125rem',
                                    fontWeight: 'bold',
                                    padding: isSmallScreen ? '8px 6px' : '12px 8px'
                                }
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '10%' }}>Código</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '25%' : '25%' }}>Visita/Evento</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '15%' }}>Fecha</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '20%' }}>Ingreso</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '20%' }}>Salida</TableHeaderCell>
                                {hasArchivos && (
                                    <TableHeaderCell sx={{ width: isSmallScreen ? '18%' : '12%' }}>Archivo</TableHeaderCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.map((item, index) => {
                                const TableCellComponent = isSmallScreen ? MobileTableCell : MuiTableCell
                                const status = getStatusIcon(item)

                                const fechaVisita = item.fecha_Visita.split(" ")[0]
                                const horaIngreso = item.fecha_Ingreso ? convertDate(item.fecha_Ingreso) : '-'
                                const horaSalida = item.fecha_Salida ? convertDate(item.fecha_Salida) : '-'
                                const clickable = isRowClickable(item)
                                const kindArchivo = getFileKind(item.url_Archivo)

                                return (
                                    <TableRow
                                        key={index}
                                        hover={item.fecha_Ingreso === ""}
                                        
                                        onClick={() => (item.fecha_Ingreso === "" && clickable) && (setItemSelect(item), setConfirm(true))}
                                        sx={{
                                            cursor: (item.fecha_Ingreso === "" && clickable) ? 'pointer' : 'default',
                                            '&:hover': {
                                                backgroundColor:theme.palette.action.hover 
                                            }
                                        }}
                                    >
                                        <TableCellComponent>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1, // Espacio entre icono y texto
                                                    maxWidth: '100%',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <Tooltip title={status.tooltip}>
                                                    {status.icon}
                                                </Tooltip>
                                                <Tooltip title={item.codigo_Acceso}>
                                                    <span style={{
                                                        textOverflow: 'ellipsis',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {item.codigo_Acceso}
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <Tooltip title={item.nombre_Visita}>
                                                <Box component="span" sx={{ display: 'inline-block', maxWidth: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                    {item.nombre_Visita}
                                                </Box>
                                            </Tooltip>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            {isSmallScreen ? fechaVisita.split('-').reverse().join('/') : fechaVisita}
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <Tooltip title={item.fecha_Ingreso ? convertDate(item.fecha_Ingreso) : 'Sin registro'}>
                                                <span>{horaIngreso}</span>
                                            </Tooltip>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <Tooltip title={item.fecha_Salida ? convertDate(item.fecha_Salida) : 'Sin registro'}>
                                                <span>{horaSalida}</span>
                                            </Tooltip>
                                        </TableCellComponent>
                                        {hasArchivos && (
                                            <TableCellComponent>
                                                {kindArchivo && (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        color="secondary"
                                                        startIcon={kindArchivo === 'video'
                                                            ? <Videocam fontSize="small" />
                                                            : <Image fontSize="small" />}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleOpenFile(item)
                                                        }}
                                                        sx={{
                                                            minWidth: 0,
                                                            p: 0.5,
                                                            textTransform: 'none',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: isSmallScreen ? '0.65rem' : '0.75rem'
                                                        }}
                                                    >
                                                        {kindArchivo === 'video' ? 'Ver video' : 'Ver foto'}
                                                    </Button>
                                                )}
                                            </TableCellComponent>
                                        )}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </Box>

                <CustomModal
                    openState={openModal}
                    onCloseHandler={handleClose}
                >
                    <Typography variant={isSmallScreen ? "body1" : "h6"}>{modalMessage}</Typography>
                    <Button
                        color='secondary'
                        variant="outlined"
                        endIcon={<Home />}
                        sx={{
                            mt: 2,
                            width: isSmallScreen ? '90%' : '50%',
                            py: 1
                        }}
                        onClick={handleClose}
                        size={isSmallScreen ? "small" : "medium"}
                    >
                        Inicio
                    </Button>
                </CustomModal>

                <Dialog open={fileViewer.open} onClose={handleCloseFile} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {fileViewer.kind === 'video' ? 'Video' : 'Foto'} del acceso {fileViewer.codigo}
                        <IconButton aria-label="cerrar" onClick={handleCloseFile} size="small">
                            <Close fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        dividers
                        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}
                    >
                        {fileError ? (
                            <Typography variant="body2" color="text.secondary">
                                No se pudo cargar el archivo.
                            </Typography>
                        ) : fileViewer.kind === 'video' ? (
                            <video
                                key={fileViewer.url}
                                src={fileViewer.url}
                                controls
                                style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8 }}
                                onError={() => setFileError(true)}
                            />
                        ) : (
                            <img
                                key={fileViewer.url}
                                src={fileViewer.url}
                                alt={'Acceso ' + fileViewer.codigo}
                                style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8 }}
                                onError={() => setFileError(true)}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </>
    )
}